// src/app/features/authSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import type { AuthUser, Child } from "../api/authApi";

// Re-export so components can import types from either place
export type { AuthUser, Child } from "../api/authApi";
export type { PublicRole } from "../api/authApi";

/* ─────────────────────────────────────────────────────────────────
   STATE
───────────────────────────────────────────────────────────────── */

export interface AuthState {
  token?: string;
  user?: AuthUser;
  /** True once localStorage has been read on app startup */
  hydrated: boolean;
}

/* ─────────────────────────────────────────────────────────────────
   STORAGE
───────────────────────────────────────────────────────────────── */

const AUTH_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const STORAGE_KEY = "auth_state";
const STORAGE_EXPIRES_KEY = "auth_expires";

const safePersist = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    console.warn("auth: localStorage unavailable", err);
  }
};

const safeClear = (...keys: string[]) => {
  keys.forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch {
      // ignore
    }
  });
};

/* ─────────────────────────────────────────────────────────────────
   INITIAL STATE
───────────────────────────────────────────────────────────────── */

const initialState: AuthState = {
  token: undefined,
  user: undefined,
  hydrated: false,
};

/* ─────────────────────────────────────────────────────────────────
   SLICE
───────────────────────────────────────────────────────────────── */

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Store token + user after login or register.
     * For parent users the backend includes children[] in the user object —
     * this is persisted as-is so the dashboard loads instantly without a
     * second /me fetch.
     */
    setCredentials(
      state,
      action: PayloadAction<{ token: string; user: AuthUser }>,
    ) {
      const { token, user } = action.payload;
      state.token = token;
      state.user = user;
      state.hydrated = true;
      safePersist(STORAGE_KEY, JSON.stringify({ token, user }));
      safePersist(STORAGE_EXPIRES_KEY, String(Date.now() + AUTH_TTL_MS));
    },

    /**
     * Replace the stored user wholesale after a profile update or /me refresh.
     * Never merge partial fields — always replace the full object.
     */
    updateUser(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
      safePersist(
        STORAGE_KEY,
        JSON.stringify({ token: state.token, user: action.payload }),
      );
    },

    /**
     * After a parent adds a child via POST /auth/add-child, the backend
     * returns the updated user with the full children array.
     * Call updateUser() with that updated user — or use this dedicated
     * action to append a single child without a full user re-fetch.
     */
    appendChild(state, action: PayloadAction<Child>) {
      if (!state.user) return;
      if (state.user.role !== "parent") return;
      state.user.children = [...(state.user.children ?? []), action.payload];
      safePersist(
        STORAGE_KEY,
        JSON.stringify({ token: state.token, user: state.user }),
      );
    },

    /**
     * Update a single child's studentStatus in the local cache
     * (e.g. after payment is confirmed and status moves to "enrolled").
     */
    updateChildStatus(
      state,
      action: PayloadAction<{
        childId: string;
        status: Child["studentStatus"];
      }>,
    ) {
      if (!state.user?.children) return;
      const idx = state.user.children.findIndex(
        (c) => c.id === action.payload.childId,
      );
      if (idx !== -1) {
        state.user.children[idx].studentStatus = action.payload.status;
        safePersist(
          STORAGE_KEY,
          JSON.stringify({ token: state.token, user: state.user }),
        );
      }
    },

    /** Clear all auth state and persisted storage (logout). */
    clearCredentials(state) {
      state.token = undefined;
      state.user = undefined;
      state.hydrated = true;
      safeClear(STORAGE_KEY, STORAGE_EXPIRES_KEY);
    },

    /**
     * Rehydrate from localStorage on app startup.
     * Call once in App.tsx before rendering protected routes.
     *
     * @example
     * useEffect(() => { dispatch(loadFromStorage()); }, []);
     */
    loadFromStorage(state) {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const expires = Number(
          localStorage.getItem(STORAGE_EXPIRES_KEY) || "0",
        );

        if (!raw || !expires || Date.now() > expires) {
          state.token = undefined;
          state.user = undefined;
          state.hydrated = true;
          safeClear(STORAGE_KEY, STORAGE_EXPIRES_KEY);
          return;
        }

        const parsed = JSON.parse(raw) as { token?: string; user?: AuthUser };
        state.token = parsed.token;
        state.user = parsed.user;
        state.hydrated = true;
      } catch {
        state.token = undefined;
        state.user = undefined;
        state.hydrated = true;
        safeClear(STORAGE_KEY, STORAGE_EXPIRES_KEY);
      }
    },
  },
});

export const {
  setCredentials,
  updateUser,
  appendChild,
  updateChildStatus,
  clearCredentials,
  loadFromStorage,
} = authSlice.actions;

export default authSlice.reducer;

/* ─────────────────────────────────────────────────────────────────
   SELECTORS
───────────────────────────────────────────────────────────────── */

export const selectToken = (state: RootState) => state.auth.token;
export const selectUser = (state: RootState) => state.auth.user;
export const selectHydrated = (state: RootState) => state.auth.hydrated;
export const selectIsAuthenticated = (state: RootState) =>
  !!state.auth.token && !!state.auth.user;
export const selectRole = (state: RootState) => state.auth.user?.role;

/** Children list — only defined when role === "parent" */
export const selectChildren = (state: RootState) =>
  state.auth.user?.children ?? [];

/** Convenience: how many children a parent has enrolled */
export const selectChildCount = (state: RootState) =>
  state.auth.user?.children?.length ?? 0;

export const selectDisplayName = (state: RootState) => {
  const u = state.auth.user;
  if (!u) return undefined;
  return `${u.firstName} ${u.lastName}`.trim();
};

export const selectFirstName = (state: RootState) => state.auth.user?.firstName;

export const selectIsEmailVerified = (state: RootState) =>
  state.auth.user?.isEmailVerified ?? false;
